import React, { useEffect, useRef, useState } from 'react';
import { queueApi } from '../services/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from './ui/table';
import { TableFillBox, tableFillHeightClass } from './ui/tableFillBox';
import Pagination from './ui/pagination';
import SearchInput from './ui/SearchInput';
import { useDebouncedValue } from './scheduledJobs/useDebouncedValue';
import { PaginationInfo } from '../types';

interface UsageData {
  user_id: string;
  email: string;
  name: string;
  blueprint_count: number;
  last_blueprint_date: string | null;
}

const BlueprintUsage: React.FC = () => {
  // Helper function to get first day of current month in YYYY-MM-DD format
  const getFirstDayOfMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getToday = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };

  const [usage, setUsage] = useState<UsageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(getFirstDayOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<keyof UsageData>('blueprint_count');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const itemsPerPage = 10;

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const isFirstRender = useRef(true);

  useEffect(() => {
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply the debounced search term (immediate for the initial mount,
  // which is already covered by the effect above).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
    fetchUsage(debouncedSearchTerm, startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchUsage = async (search?: string, start?: string, end?: string) => {
    try {
      setLoading(true);
      const data = await queueApi.getBlueprintUsage(
        search !== undefined ? search : searchTerm,
        start !== undefined ? start : startDate,
        end !== undefined ? end : endDate
      );
      setUsage(data.usage);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch blueprint usage');
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    setCurrentPage(1);
    fetchUsage(searchTerm, value, endDate);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    setCurrentPage(1);
    fetchUsage(searchTerm, startDate, value);
  };

  const handleReset = () => {
    setSearchTerm('');
    const firstDay = getFirstDayOfMonth();
    const today = getToday();
    setStartDate(firstDay);
    setEndDate(today);
    setCurrentPage(1);
    fetchUsage('', firstDay, today);
  };

  // Sorting logic
  const handleSort = (field: keyof UsageData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const sortedUsage = [...usage].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle null values
    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;

    // Convert to appropriate types for comparison
    if (sortField === 'blueprint_count') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    } else if (sortField === 'last_blueprint_date') {
      aValue = aValue ? new Date(aValue as string).getTime() : 0;
      bValue = bValue ? new Date(bValue as string).getTime() : 0;
    } else {
      // String comparison (case-insensitive)
      aValue = (aValue as string).toLowerCase();
      bValue = (bValue as string).toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Calculate pagination
  const totalPages = Math.ceil(sortedUsage.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = sortedUsage.slice(startIndex, endIndex);
  // Full page (or more) FILLS the table box down to the pagination footer;
  // a sparse result set HUGS its content (shared TableFillBox contract,
  // deepcontext-wbra).
  const isFullPage = sortedUsage.length >= itemsPerPage;

  const goToPage = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const paginationInfo: PaginationInfo = {
    page: currentPage,
    limit: itemsPerPage,
    totalCount: sortedUsage.length,
    totalPages,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
    nextPage: currentPage < totalPages ? currentPage + 1 : null,
    previousPage: currentPage > 1 ? currentPage - 1 : null,
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SortIcon = ({ field }: { field: keyof UsageData }) => {
    if (sortField !== field) {
      return null;
    }
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  if (loading && usage.length === 0) {
    return <div className="text-center py-8">Loading blueprint usage...</div>;
  }

  if (error) {
    return (
      <div className="bg-risk-bg border border-risk rounded-md p-4">
        <p className="text-risk">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-2.5 mb-4">
        <SearchInput
          className="flex-1"
          placeholder="Search by name or email…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <input
          type="date"
          value={startDate}
          onChange={(e) => handleStartDateChange(e.target.value)}
          className="rounded-full border border-line bg-surface px-3.5 h-[42px] text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-ink-muted">–</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => handleEndDateChange(e.target.value)}
          className="rounded-full border border-line bg-surface px-3.5 h-[42px] text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <Button variant="ghost" onClick={handleReset} disabled={loading}>
          Reset
        </Button>
      </div>

      {/* Results Table */}
      {usage.length === 0 ? (
        <div className="text-center py-8 text-ink-muted">
          No blueprint usage data found
        </div>
      ) : (
        <TableFillBox
          isFullPage={isFullPage}
          footer={totalPages > 1 ? <Pagination pagination={paginationInfo} onPageChange={goToPage} /> : undefined}
          className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-sm"
        >
          <Table className={tableFillHeightClass(isFullPage)}>
            <TableHead>
              <tr>
                <TableHeaderCell className="cursor-pointer select-none" onClick={() => handleSort('email')}>
                  <div className="flex items-center">
                    User Email
                    <SortIcon field="email" />
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="cursor-pointer select-none" onClick={() => handleSort('name')}>
                  <div className="flex items-center">
                    User Name
                    <SortIcon field="name" />
                  </div>
                </TableHeaderCell>
                <TableHeaderCell
                  className="text-center cursor-pointer select-none"
                  onClick={() => handleSort('blueprint_count')}
                >
                  <div className="flex items-center justify-center">
                    Blueprint Count
                    <SortIcon field="blueprint_count" />
                  </div>
                </TableHeaderCell>
                <TableHeaderCell className="cursor-pointer select-none" onClick={() => handleSort('last_blueprint_date')}>
                  <div className="flex items-center">
                    Last Blueprint
                    <SortIcon field="last_blueprint_date" />
                  </div>
                </TableHeaderCell>
              </tr>
            </TableHead>
            <TableBody>
              {currentUsers.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell className="whitespace-nowrap">
                    {user.email}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {user.name || '-'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-center">
                    <Badge>{user.blueprint_count}</Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(user.last_blueprint_date)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableFillBox>
      )}
    </div>
  );
};

export default BlueprintUsage;
